import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getAdminSocietyFlatDetail,
  AdminSocietyFlatDetail as FlatDetailData,
  AdminSocietyActivityLog,
  getBillCategories,
  getFlatLedgerBalances,
  setFlatOpeningBalance,
  rebuildFlatLedger,
  BillCategoryItem,
  CategoryLedgerBalance,
} from '../services/api';
import './AdminSocietyDetail.css';

const approvalClass = (status: string): string => {
  if (status === 'approved') return 'approval-approved';
  if (status === 'rejected') return 'approval-rejected';
  return 'approval-pending';
};

const ActivityList: React.FC<{ logs: AdminSocietyActivityLog[]; empty: string }> = ({
  logs,
  empty,
}) => (
  <ul className="admin-society-detail-activity">
    {logs.length === 0 ? (
      <li className="admin-society-detail-empty">{empty}</li>
    ) : (
      logs.map((log, idx) => (
        <li key={`${log.occurred_at}-${idx}`} className="admin-society-detail-activity-item">
          <div className="activity-time">{new Date(log.occurred_at).toLocaleString()}</div>
          <div className="activity-body">
            <span className={`activity-type activity-type-${log.event_type}`}>
              {log.event_type.replace(/_/g, ' ')}
            </span>
            <div className="activity-title">{log.title}</div>
            {log.detail && <div className="activity-detail">{log.detail}</div>}
            {log.actor_name && <div className="activity-actor">By {log.actor_name}</div>}
          </div>
        </li>
      ))
    )}
  </ul>
);

const AdminSocietyFlatDetail: React.FC = () => {
  const { societyId: sid, flatId: fid } = useParams<{ societyId: string; flatId: string }>();
  const societyId = sid ? parseInt(sid, 10) : NaN;
  const flatId = fid ? parseInt(fid, 10) : NaN;
  const [data, setData] = useState<FlatDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<BillCategoryItem[]>([]);
  const [balances, setBalances] = useState<CategoryLedgerBalance[]>([]);
  const [obBillType, setObBillType] = useState('maintenance');
  const [obPrincipal, setObPrincipal] = useState('');
  const [obInterest, setObInterest] = useState('');
  const [obAdvance, setObAdvance] = useState('0');
  const [obDueDate, setObDueDate] = useState('');
  const [obSaving, setObSaving] = useState(false);
  const [obMessage, setObMessage] = useState('');

  useEffect(() => {
    if (isNaN(societyId) || isNaN(flatId)) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await getAdminSocietyFlatDetail(societyId, flatId);
        if (!cancelled) {
          setData(res.data);
          const [catRes, balRes] = await Promise.all([
            getBillCategories(societyId),
            getFlatLedgerBalances(flatId, societyId),
          ]);
          if (!cancelled) {
            setCategories(catRes.data.filter((c) => c.is_active));
            setBalances(balRes.data);
            if (catRes.data.length > 0) {
              setObBillType(catRes.data[0]!.bill_type_key);
            }
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load flat');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [societyId, flatId]);

  if (loading) {
    return <div className="admin-society-detail-loading">Loading flat…</div>;
  }

  if (error || !data) {
    return (
      <div className="admin-society-detail">
        <Link to={isNaN(societyId) ? '/admin/societies' : `/admin/societies/${societyId}`} className="admin-society-detail-back">
          ← Back to society
        </Link>
        <div className="admin-society-detail-error">{error || 'Not found'}</div>
      </div>
    );
  }

  const { society, flat, flat_activity_logs, per_user_activity } = data;
  const societyBack = `/admin/societies/${society.id}`;
  const flatLabel = [flat.block_name, flat.flat_number].filter(Boolean).join(' · ') || flat.flat_number;

  const formatMoney = (n: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);

  const refreshBalances = async () => {
    const balRes = await getFlatLedgerBalances(flatId, societyId);
    setBalances(balRes.data);
  };

  const handleSaveOpeningBalance = async (e: React.FormEvent) => {
    e.preventDefault();
    setObSaving(true);
    setObMessage('');
    try {
      await setFlatOpeningBalance(flatId, {
        society_id: societyId,
        bill_type: obBillType,
        principal_outstanding: Number(obPrincipal) || 0,
        interest_outstanding: Number(obInterest) || 0,
        advance_balance: Number(obAdvance) || 0,
        due_date: obDueDate || null,
      });
      setObMessage('Opening balance saved.');
      await refreshBalances();
    } catch (err: unknown) {
      setObMessage(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setObSaving(false);
    }
  };

  const handleRebuild = async (billType: string) => {
    if (!window.confirm(`Rebuild ledger for ${billType} from open bills?`)) return;
    setObSaving(true);
    setObMessage('');
    try {
      await rebuildFlatLedger(flatId, societyId, billType);
      setObMessage(`Rebuilt ${billType} from open bills.`);
      await refreshBalances();
    } catch (err: unknown) {
      setObMessage(err instanceof Error ? err.message : 'Rebuild failed');
    } finally {
      setObSaving(false);
    }
  };

  return (
    <div className="admin-society-detail">
      <Link to={societyBack} className="admin-society-detail-back">
        ← Back to {society.name}
      </Link>

      <header className="admin-society-detail-header">
        <h2>
          {society.name} — Flat {flatLabel}
        </h2>
        <p className="admin-society-detail-meta">
          Floor {flat.floor}
          {flat.area != null ? ` · Area ${flat.area}` : ''}
        </p>
      </header>

      <section className="admin-society-detail-card">
        <h3>Flat details</h3>
        <dl className="admin-society-detail-dl">
          <div>
            <dt>Block</dt>
            <dd>{flat.block_name ?? '—'}</dd>
          </div>
          <div>
            <dt>Flat number</dt>
            <dd>{flat.flat_number}</dd>
          </div>
          <div>
            <dt>Floor</dt>
            <dd>{flat.floor}</dd>
          </div>
          <div>
            <dt>Area</dt>
            <dd>{flat.area != null ? flat.area : '—'}</dd>
          </div>
        </dl>
      </section>

      <section className="admin-society-detail-card">
        <h3>Billing ledger</h3>
        <p className="admin-society-detail-section-intro">
          Set opening balance per category or rebuild from open society bills (fixes double-counted balances).
        </p>
        {balances.length > 0 && (
          <div className="admin-society-detail-table-wrap" style={{ marginBottom: 16 }}>
            <table className="admin-society-detail-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Principal</th>
                  <th>Interest</th>
                  <th>Advance</th>
                  <th>Total due</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={b.bill_type}>
                    <td>{b.bill_type}</td>
                    <td>{formatMoney(b.principal_outstanding)}</td>
                    <td>{formatMoney(b.interest_outstanding)}</td>
                    <td>{formatMoney(b.advance_balance)}</td>
                    <td className="cell-strong">{formatMoney(b.total_due)}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-society-detail-back"
                        style={{ border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}
                        disabled={obSaving}
                        onClick={() => handleRebuild(b.bill_type)}
                      >
                        Rebuild
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <form onSubmit={handleSaveOpeningBalance} className="admin-society-detail-dl" style={{ gap: 12 }}>
          <div>
            <label htmlFor="ob-cat">Category</label>
            <select
              id="ob-cat"
              value={obBillType}
              onChange={(e) => setObBillType(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.bill_type_key} value={c.bill_type_key}>
                  {c.name}
                </option>
              ))}
              {categories.length === 0 && <option value="maintenance">Maintenance</option>}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label htmlFor="ob-principal">Principal outstanding</label>
              <input id="ob-principal" type="number" min={0} step={0.01} value={obPrincipal} onChange={(e) => setObPrincipal(e.target.value)} required />
            </div>
            <div>
              <label htmlFor="ob-interest">Interest outstanding</label>
              <input id="ob-interest" type="number" min={0} step={0.01} value={obInterest} onChange={(e) => setObInterest(e.target.value)} required />
            </div>
            <div>
              <label htmlFor="ob-advance">Advance credit</label>
              <input id="ob-advance" type="number" min={0} step={0.01} value={obAdvance} onChange={(e) => setObAdvance(e.target.value)} />
            </div>
            <div>
              <label htmlFor="ob-due">Due date (optional)</label>
              <input id="ob-due" type="date" value={obDueDate} onChange={(e) => setObDueDate(e.target.value)} />
            </div>
          </div>
          <button type="submit" disabled={obSaving} className="approval-pill approval-approved" style={{ width: 'fit-content', border: 'none', cursor: 'pointer' }}>
            {obSaving ? 'Saving…' : 'Save opening balance'}
          </button>
          {obMessage && <p className="admin-society-detail-section-intro">{obMessage}</p>}
        </form>
      </section>

      <section className="admin-society-detail-card">
        <h3>Residents</h3>
        <p className="admin-society-detail-section-intro">
          Members linked to this flat in the app. Primary flat and approval status are shown per account.
        </p>
        <div className="admin-society-detail-table-wrap">
          <table className="admin-society-detail-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Primary</th>
                <th>Approval</th>
                <th>Linked</th>
              </tr>
            </thead>
            <tbody>
              {flat.users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-society-detail-empty">
                    No residents mapped to this flat.
                  </td>
                </tr>
              ) : (
                flat.users.map((u) => (
                  <tr key={u.id}>
                    <td className="cell-strong">{u.name}</td>
                    <td className="cell-contact">{[u.email, u.phone].filter(Boolean).join(' · ') || '—'}</td>
                    <td>{u.is_primary ? 'Yes' : '—'}</td>
                    <td>
                      <span className={`approval-pill ${approvalClass(u.approval_status)}`}>{u.approval_status}</span>
                    </td>
                    <td className="cell-muted">
                      {u.mapped_at ? new Date(u.mapped_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-society-detail-card">
        <h3>Flat activity</h3>
        <p className="admin-society-detail-section-intro">
          Logins and flat links for residents of this flat, plus guest visits registered for this flat.
        </p>
        <ActivityList logs={flat_activity_logs} empty="No flat-level activity yet." />
      </section>

      {per_user_activity.map((block) => (
        <section key={block.user_id} className="admin-society-detail-card">
          <h3>Activity — {block.user_name}</h3>
          <p className="admin-society-detail-section-intro">
            Logins and society membership changes for this resident (all flats in this society).
          </p>
          <ActivityList logs={block.activity_logs} empty="No activity recorded for this user." />
        </section>
      ))}
    </div>
  );
};

export default AdminSocietyFlatDetail;
